<template>
  <div class="filters-bar">
    <div class="filter-item">
      <select
        class="filter-select"
        v-model="country"
        @change="onCountryFilter"
      >
        <option value="">{{ $t('forms.allCountries') }}</option>
        <option
          v-for="c in countryOptions"
          :key="c.slug"
          :value="c.slug"
        >
          {{ c.name }}
        </option>
      </select>
    </div>
    <div class="search-group">
      <div class="search-container">
        <input
          type="text"
          class="search-input"
          :placeholder="$t('forms.searchGamesPlaceholder')"
          v-model="search"
          @input="onSearch"
        />
        <button class="search-button" type="button">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 0 0 1.48-5.34c-.47-2.78-2.79-5-5.59-5.34a6.505 6.505 0 0 0-7.27 7.27c.34 2.8 2.56 5.12 5.34 5.59a6.5 6.5 0 0 0 5.34-1.48l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0 .41-.41.41-1.08 0-1.49L15.5 14zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
              fill="currentColor"
            />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, computed, defineEmits } from "vue";
import { useUtilitiesStore } from "@/stores/utilities";

const emit = defineEmits<{
  (e: "search", value: string): void;
  (e: "filterCountry", value: string): void;
}>();

const search = ref("");
const country = ref("");

const utilitiesStore = useUtilitiesStore();

onMounted(() => {
  // fetchCountries now has caching, safe to call multiple times
  utilitiesStore.fetchCountries();
});

const countryOptions = computed(() => utilitiesStore.countries);

const onSearch = () => emit("search", search.value);
const onCountryFilter = () => emit("filterCountry", country.value);

watch([search, country], () => {
  emit("search", search.value);
  emit("filterCountry", country.value);
});
</script>

<script lang="ts">
export default {
  name: "LotteryFilters",
};
</script>

<style scoped>
.filters-bar {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  flex: 1;
  width: 100%;
}

/* When used in date-filter-section, remove bottom margin */
.date-filter-section .filters-bar {
  margin-bottom: 0;
  flex: 1;
  min-width: 0;
}

.filter-item {
  flex: 1;
  min-width: 0;
  max-width: 300px;
}

.search-group {
  flex: 1;
  min-width: 0;
  max-width: 300px;
}

.search-container {
  display: flex;
  align-items: center;
  background: var(--bg-search-container, var(--bg-secondary));
  border-radius: 12px;
  border: 2.57px solid var(--border-color);
  padding: 8px;
  width: 100%;
  height: 60px;
  box-sizing: border-box;
  min-width: 0;
}

.search-input {
  flex: 1;
  background: var(--bg-search-container, var(--bg-secondary));
  border: none;
  color: var(--text-primary);
  font-size: 16px;
  outline: none;
  padding: 8px 16px;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
}

.search-input::placeholder {
  color: var(--text-light);
}

.search-button {
  background: var(--bg-search-button, var(--lottery-card-gradient));
  border: none;
  color: var(--text-button, var(--text-on-card-contrast, var(--text-white)));
  cursor: pointer;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  min-width: 48px;
  height: 44px;
  flex-shrink: 0;
}


/* Large screens (1200px+) - Enhanced layout */
@media (min-width: 1200px) {
  .filters-bar {
    gap: 20px;
    margin-bottom: 32px;
    justify-content: center;
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
  }
  
  .filter-item {
    max-width: 350px;
  }
  
  .search-group {
    max-width: 350px;
  }
  
  .search-container {
    height: 64px;
    padding: 10px;
  }
  
  .search-input {
    font-size: 17px;
    padding: 10px 18px;
  }
  
  .search-button {
    min-width: 52px;
    height: 48px;
    padding: 14px;
  }
}

/* Medium screens (1024px - 1199px) - Balanced layout */
@media (min-width: 1024px) and (max-width: 1199px) {
  .filters-bar {
    gap: 18px;
    margin-bottom: 28px;
    justify-content: center;
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;
  }
  
  .filter-item {
    max-width: 320px;
  }
  
  .search-group {
    max-width: 320px;
  }
  
  .search-container {
    height: 62px;
    padding: 9px;
  }
  
  .search-input {
    font-size: 16px;
    padding: 9px 17px;
  }
  
  .search-button {
    min-width: 50px;
    height: 46px;
    padding: 13px;
  }
}

/* Tablet/iPad (768px - 1023px) - Optimized for tablet */
@media (min-width: 768px) and (max-width: 1023px) {
  .filters-bar {
    gap: 16px;
    margin-bottom: 24px;
    flex-direction: row;
    justify-content: space-between;
    align-items: stretch;
    flex-wrap: nowrap;
  }
  
  .filter-item {
    flex: 1;
    max-width: none;
    min-width: 0;
  }
  
  .search-group {
    flex: 1;
    max-width: none;
    min-width: 0;
  }
  
  .search-container {
    height: 58px;
    padding: 8px;
  }
  
  .search-input {
    font-size: 15px;
    padding: 8px 16px;
  }
  
  .search-button {
    min-width: 46px;
    height: 42px;
    padding: 11px;
  }
}

/* Mobile (≤767px)t */
@media (max-width: 767px) {
  .filters-bar {
    flex-direction: row;
    gap: 12px;
    margin-bottom: 20px;
    align-items: stretch;
    flex-wrap: nowrap;
  }
  
  .filter-item {
    flex: 1;
    max-width: none;
    min-width: 0;
  }
  
  .search-group {
    flex: 1;
    max-width: none;
    min-width: 0;
  }
  
  .search-container {
    height: 50px;
    padding: 6px;
    border-radius: 10px;
    border-width: 2px;
  }
  
  .search-input {
    font-size: 14px;
    padding: 6px 12px;
  }
  
  .search-button {
    min-width: 40px;
    height: 38px;
    padding: 8px;
  }
  
  .search-button svg {
    width: 18px;
    height: 18px;
  }
  
  .filter-select {
    height: 50px !important;
    font-size: 14px !important;
    padding: 12px 16px !important;
    border-radius: 10px !important;
    border-width: 2px !important;
  }
  
  .filter-select option {
    font-size: 14px !important;
    padding: 8px 12px !important;
  }
}

/* Small mobile (≤480px) - Compact layout */
@media (max-width: 480px) {
  .filters-bar {
    gap: 10px;
    margin-bottom: 16px;
  }
  
  .search-container {
    height: 46px;
    padding: 5px;
    border-radius: 8px;
  }
  
  .search-input {
    font-size: 13px;
    padding: 5px 10px;
  }
  
  .search-button {
    min-width: 36px;
    height: 36px;
    padding: 6px;
  }
  
  .search-button svg {
    width: 16px;
    height: 16px;
  }
  
  .filter-select {
    height: 46px !important;
    font-size: 13px !important;
    padding: 10px 14px !important;
    border-radius: 8px !important;
  }
  
  .filter-select option {
    font-size: 13px !important;
    padding: 6px 10px !important;
  }
}

.filter-select {
  width: 100%;
  background: var(--bg-search-container, var(--bg-select-color, var(--bg-secondary)));
  border-radius: 12px;
  border: 2.57px solid var(--border-color);
  color: var(--text-primary);
  font-size: 14px;
  padding: 20px;
  cursor: pointer;
  outline: none;
  text-transform: uppercase;
  box-sizing: border-box;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  height: 60px;
  background-image: var(--bg-filter-select-img, url("data:image/svg+xml,%3Csvg width='14' height='8' viewBox='0 0 14 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L7 7L13 1' stroke='%231c2536' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E"));
  background-repeat: no-repeat;
  background-position: right 20px center;
  padding-right: 48px;
}
/*  */
.filter-select option {
  background: var(--bg-search-container, var(--bg-secondary));
  color: var(--text-primary);
}
</style>